import * as React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import { Button } from '@helpcenter/analytics-components';
import type { WrappedComponentProps } from 'react-intl-next';
import { injectIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { AsyncLoadedPasswordStrengthMeter } from 'view/common/password-strength-meter';
import WatchIcon from '@atlaskit/icon/glyph/watch';
import WatchFilledIcon from '@atlaskit/icon/glyph/watch-filled';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { ErrorIndication } from '../error-indication';
import messages from './messages';

interface PasswordFieldProps {
    id: string;
    maxLength?: number;
    minLength?: number;
    value?: string;
    testId?: string;
    onChange: (updatedPassword: string) => void;
    isDisabled: boolean;
    autoFocus?: boolean;
    weakPasswords?: string[];
    errorMessage?: string;
    isInvalid: boolean;
    hideStrengthMeter?: boolean;
    onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
}

interface PasswordFieldState {
    showPassword: boolean;
}

export class PasswordField extends React.Component<PasswordFieldProps & WrappedComponentProps, PasswordFieldState> {
    state = {
        showPassword: false,
    };

    onChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        this.props.onChange(e.target.value);
    };

    onShowPasswordToggle = () => {
        this.setState((prevState) => ({
            showPassword: !prevState.showPassword,
        }));
    };

    renderShowHideButton = () => {
        const { intl, isDisabled } = this.props;
        const { showPassword } = this.state;

        const buttonActionPrefix = showPassword ? 'hide' : 'show';
        const togglePasswordButtonDescription = showPassword
            ? intl.formatMessage(messages.hidePassword)
            : intl.formatMessage(messages.showPassword);

        const icon = showPassword ? (
            <WatchFilledIcon testId="watch-filled-icon" label={togglePasswordButtonDescription} />
        ) : (
            <WatchIcon testId="watch-icon" label={togglePasswordButtonDescription} />
        );

        return (
            <Tooltip content={togglePasswordButtonDescription}>
                <Button
                    actionSubjectId={`${buttonActionPrefix}-password`}
                    appearance="subtle"
                    isDisabled={isDisabled}
                    onClick={this.onShowPasswordToggle}
                    iconBefore={<IconContainer>{icon}</IconContainer>}
                />
            </Tooltip>
        );
    };

    render() {
        const {
            autoFocus,
            intl,
            id,
            maxLength,
            value,
            isDisabled,
            isInvalid,
            weakPasswords,
            errorMessage,
            hideStrengthMeter,
            testId,
            onKeyDown,
        } = this.props;
        const { showPassword } = this.state;
        const passwordFieldType = showPassword ? 'text' : 'password';
        const shouldRenderStrengthMeter = !hideStrengthMeter;

        di(AsyncLoadedPasswordStrengthMeter);

        return (
            <>
                <Textfield
                    id={id}
                    testId={testId}
                    type={passwordFieldType}
                    maxLength={maxLength}
                    onChange={this.onChange}
                    isDisabled={isDisabled}
                    // Disabling existing violations, should be fixed when revisited.
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus={autoFocus}
                    isInvalid={isInvalid}
                    value={value}
                    elemAfterInput={this.renderShowHideButton()}
                    onKeyDown={onKeyDown}
                />
                {isInvalid && errorMessage && <ErrorIndication errorMessage={errorMessage} />}
                {shouldRenderStrengthMeter && (
                    <AsyncLoadedPasswordStrengthMeter intl={intl} password={value} scoreWords={weakPasswords} />
                )}
            </>
        );
    }
}

export default injectIntl(PasswordField);

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const IconContainer = styled.div`
    padding-right: ${token('space.025', '2px')};
    margin-left: ${token('space.negative.025', '-2px')};
`;
